/**
 * Admin Prompts Overview Page
 * Lists all registered prompts with filtering by module.
 */

import { useState, useEffect } from 'react';
import apiClient from '../../lib/api';
import PromptTable, { type PromptListItem } from '../../components/admin/PromptTable';

export default function AdminPromptsPage() {
  const [prompts, setPrompts] = useState<PromptListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchPrompts = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        if (moduleFilter) params.set('module', moduleFilter);
        if (search) params.set('search', search);
        const response = await apiClient.get(`/admin/prompts?${params}`);
        setPrompts(response.data.prompts);
      } catch (err) {
        console.error('Failed to fetch prompts:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPrompts();
  }, [moduleFilter, search]);

  // Extract unique modules
  const modules = [...new Set(prompts.map((p) => p.module))].sort();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-black dark:text-white">Prompt Management</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Beheer alle AI prompts. Override code defaults, test met variabelen, vergelijk versies.
        </p>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op naam of key..."
          className="flex-1 px-3 py-2 text-sm border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white focus:outline-none focus:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
        />
        <select
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          className="px-3 py-2 text-sm border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white"
        >
          <option value="">Alle modules</option>
          {modules.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="flex gap-4 text-sm">
        <span className="text-gray-600 dark:text-gray-400">
          {prompts.length} prompts
        </span>
        <span className="text-green-700 dark:text-green-400">
          {prompts.filter((p) => p.isOverridden).length} overrides actief
        </span>
      </div>

      <PromptTable prompts={prompts} isLoading={isLoading} />
    </div>
  );
}
